import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import BaseModalComponent, { StyledBaseModal } from '../BaseModal.component';
import ButtonComponent, { StyledButton } from '../../Button.component';
import FormLayoutComponent from '../../Form/FormLayout.component';
import InputComponent from '../../Input/Input.component';
import TextareaComponent from '../../Input/Textarea.component';
import DatepickerModalComponent from '../DatepickerModal.component';
import SelectComponent, {
  SelectOptionProps,
} from '../../Input/Select.component';
import {
  StackedModalProps,
  useModalManager,
} from '../../../hooks/ModalManager.hook';
import { useToast } from '../../../hooks/useToast';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import ApiWrapper from '../../../hooks/useApi.hook';
import {
  createCalendarEventThunk,
  getCalendarEventThunk,
  refreshPersonInterviewsThunk,
  updateCalendarEventThunk,
} from '../../../redux/Calendar/Calendar.thunk';
import { upsertInterviewLocalAction } from '../../../redux/Calendar/Calendar.slice';
import candidateService from '../../../redux/Candidate/Candidate.service';
import {
  formatDateTimeForApi,
  getAttachmentFileName,
  getCloudCvs,
} from '../../../redux/Calendar/Calendar.mapper';
import attachmentService from '../../../redux/Attachment/Attachment.service';
import {
  CalendarEventFormData,
  CalendarEventParticipant,
  CalendarInterview,
  ParticipantRole,
} from '../../../redux/Calendar/Calendar.type';
import { PersonRecord } from '../../../redux/Candidate/Record/Record.type';
import { Attachment } from '../../../redux/Attachment/Attachment.type';
import { Employee } from '../../../redux/Employee/Employee.type';
import { DATE_TIME_FORMAT } from '../../../constants/date.contant';
import { textAriasMaxLength } from '../../../constants/textArias.constant';
import { REGEXP } from '../../../constants/regexp.constant';
import CrossIcon from '../../Icons/Cross.icon';
import CheckIcon from '../../Icons/Check.icon';
import PenIcon from '../../Icons/Pen.icon';
import { EventStatusCode } from '../../../redux/Event/Status/EventStatus.type';
import UploadCvInlineModal from './UploadCvInlineModal.component';
import { useCalendarCvUpload } from '../../../hooks/useCalendarCvUpload.hook';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const EMPTY_CANDIDATE_ATTACHMENTS: Attachment[] = [];

export interface GoogleCalendarEventModalProps extends StackedModalProps {
  eventStatusCode?: EventStatusCode;
  record: PersonRecord;
  mode?: 'create' | 'edit';
  googleEventId?: string;
  interviewId?: number;
  candidateName?: string;
  candidateEmail?: string;
  candidateAttachments?: Attachment[];
  onSaved?: () => void;
}

const StyledGoogleCalendarEventModal = styled.div`
  ${StyledBaseModal} {
    width: 550px;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px 0 0 0;
`;

const StyledFooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;

const StyledFormErrors = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  color: ${({ theme }) => theme.colors.RED.R400};
  font-size: 12px;
  line-height: 1.4;
`;

const StyledParticipantsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledInputParticipantContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledParticipantItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.BORDER};
  background: ${({ theme }) => theme.colors.BG};
`;

const StyledParticipantInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledParticipantName = styled.div`
  font-weight: 500;
  font-size: 13px;
`;

const StyledParticipantEmail = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.LABEL};
`;

const StyledParticipantRole = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.LABEL};
  margin-left: auto;
  margin-right: 10px;
`;

const StyledRemoveButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  color: hsl(0, 0%, 80%);

  &:hover {
    color: hsl(0, 0%, 60%);
  }

  svg {
    fill: currentColor;
    width: 12px;
  }
`;

const StyledParticipantActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledParticipantEmailEdit = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledLabel = styled.div`
  font-weight: 500;
  gap: 5px;
`;

const StyledCvSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StyledCvItem = styled(StyledParticipantItem)`
  gap: 12px;
  flex-wrap: wrap;

  label {
    min-width: 0;
    flex: 1 1 200px;
    overflow-wrap: anywhere;
  }
`;

const StyledCvList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledEmptyHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.LABEL};
`;

const StyledCvUploadError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.RED.R400};
`;

const DEFAULT_INTERVIEW_DURATION_HOURS = 1;
const MAX_EXTRA_PARTICIPANTS = 2;

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

const getTimeZoneOptions = (defaultTimeZone: string): string[] => {
  const unique = new Set<string>([defaultTimeZone, 'UTC']);
  const supportedValuesOf = (
    Intl as typeof Intl & {
      supportedValuesOf?: (type: string) => string[];
    }
  ).supportedValuesOf;
  if (typeof supportedValuesOf === 'function') {
    supportedValuesOf('timeZone').forEach(tz => unique.add(tz));
  }
  return Array.from(unique).sort((a, b) => a.localeCompare(b));
};

const resolveParticipantRole = (
  email: string,
  candidateEmail: string,
  record: PersonRecord,
): ParticipantRole => {
  const normalized = normalizeEmail(email);

  if (candidateEmail && normalized === normalizeEmail(candidateEmail)) {
    return ParticipantRole.CANDIDATE;
  }
  if (record.hr?.email && normalized === normalizeEmail(record.hr.email)) {
    return ParticipantRole.HR;
  }
  if (
    record.techSpecialists?.some(
      techSpecialist =>
        techSpecialist.email &&
        normalized === normalizeEmail(techSpecialist.email),
    )
  ) {
    return ParticipantRole.TECH_SPECIALIST;
  }
  return ParticipantRole.PARTICIPANT;
};

const buildParticipants = (
  eventStatusCode: EventStatusCode | undefined,
  record: PersonRecord,
  candidateName?: string,
  candidateEmail?: string,
): CalendarEventParticipant[] => {
  const participants: CalendarEventParticipant[] = [];

  if (candidateEmail || candidateName) {
    participants.push({
      email: candidateEmail || '',
      fullName: candidateName || '',
      role: ParticipantRole.CANDIDATE,
    });
  }

  const addEmployee = (
    employee: Employee | undefined,
    role: CalendarEventParticipant['role'],
  ) => {
    if (!employee) return;
    const email = employee.email || '';
    participants.push({
      email,
      fullName: employee.fullName,
      role,
    });
  };

  addEmployee(record.hr, ParticipantRole.HR);
  if (
    eventStatusCode === EventStatusCode.INTERVIEW_PLANNED &&
    record.techSpecialists?.length
  ) {
    record.techSpecialists.forEach(ts =>
      addEmployee(ts, ParticipantRole.TECH_SPECIALIST),
    );
  }

  return participants;
};

const GoogleCalendarEventModalComponent = ({
  eventStatusCode,
  record,
  mode = 'create',
  googleEventId,
  interviewId,
  candidateName,
  candidateEmail,
  candidateAttachments = EMPTY_CANDIDATE_ATTACHMENTS,
  onSaved,
}: GoogleCalendarEventModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { closeModal, openModal } = useModalManager();
  const { successToast, errorToast } = useToast();
  const isEdit = mode === 'edit';

  const [candidateCvs, setCandidateCvs] = useState(candidateAttachments);
  const [isCandidateCvsLoading, setIsCandidateCvsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setCandidateCvs(candidateAttachments);
    setIsCandidateCvsLoading(true);

    candidateService
      .getPreviewInfoIsolated(record.personId)
      .then(response => {
        if (cancelled) {
          return;
        }

        const fromPreview = response.data?.data?.attachments ?? [];
        const byUuid = new Map<string, Attachment>();

        [...candidateAttachments, ...fromPreview].forEach(attachment => {
          if (attachment?.uuid) {
            byUuid.set(attachment.uuid, attachment);
          }
        });

        setCandidateCvs(Array.from(byUuid.values()));
      })
      .catch(() => {
        // CVs received by the caller remain available if refresh fails.
      })
      .finally(() => {
        if (!cancelled) {
          setIsCandidateCvsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [candidateAttachments, record.personId]);

  const defaultStartAt = record.date
    ? record.date * 1000
    : dayjs().add(1, 'hour').startOf('hour').valueOf();
  const defaultEndAt =
    defaultStartAt + DEFAULT_INTERVIEW_DURATION_HOURS * 60 * 60 * 1000;

  const [summary, setSummary] = useState(
    `${t('calendarEvent.defaultSummary')}: ${candidateName || ''}`,
  );
  const [description, setDescription] = useState('');
  const [startAt, setStartAt] = useState(defaultStartAt);
  const [endAt, setEndAt] = useState(defaultEndAt);
  const [participants, setParticipants] = useState<CalendarEventParticipant[]>(
    buildParticipants(eventStatusCode, record, candidateName, candidateEmail),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEventReady, setIsEventReady] = useState(!isEdit);
  const [eventLoadError, setEventLoadError] = useState(false);
  const [extraParticipantEmail, setExtraParticipantEmail] = useState('');
  const [
    extraParticipantEmailValidationMessage,
    setExtraParticipantEmailValidationMessage,
  ] = useState('');
  const [editingParticipantIndex, setEditingParticipantIndex] = useState<
    number | null
  >(null);
  const [editingEmailValue, setEditingEmailValue] = useState('');
  const [editingEmailError, setEditingEmailError] = useState('');
  const [hasPendingCv, setHasPendingCv] = useState(false);
  const [cvNames, setCvNames] = useState<Record<string, string>>({});
  const [downloadingCv, setDownloadingCv] = useState<string | null>(null);
  const downloadInFlight = useRef(false);
  const [selectedCvUuids, setSelectedCvUuids] = useState<string[]>([]);
  const defaultTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );
  const [selectedTimeZone, setSelectedTimeZone] = useState(defaultTimeZone);

  const recordRef = useRef(record);
  recordRef.current = record;
  const errorToastRef = useRef(errorToast);
  errorToastRef.current = errorToast;
  const tRef = useRef(t);
  tRef.current = t;

  const recordParticipantsKey = useMemo(() => {
    const hr = record.hr
      ? `${record.hr.email || ''}|${record.hr.fullName || ''}`
      : '';
    const tech = (record.techSpecialists || [])
      .map(ts => `${ts.email || ''}|${ts.fullName || ''}`)
      .join(';');
    return `${hr}#${tech}`;
  }, [record.hr, record.techSpecialists]);

  const toggleCv = (uuid: string) => {
    setSelectedCvUuids(prev =>
      prev.includes(uuid)
        ? prev.filter(selectedUuid => selectedUuid !== uuid)
        : [...prev, uuid],
    );
  };

  const { handleUploadCv, isUploadingCv, cvUploadError, savedCvs } =
    useCalendarCvUpload({
      record,
      isSubmitting,
      onUploaded: (uuid, fileName) => {
        setCvNames(prev => ({ ...prev, [uuid]: fileName }));
        setSelectedCvUuids(prev =>
          prev.includes(uuid) ? prev : [...prev, uuid],
        );
        setCandidateCvs(prev => {
          if (prev.some(attachment => attachment.uuid === uuid)) {
            return prev;
          }
          return [
            ...prev,
            {
              uuid,
              type: 'RESUME',
              isCloud: true,
              isPrivate: false,
              url: fileName,
              createdAt: Date.now(),
            } as Attachment,
          ];
        });
      },
    });

  const cloudCvs = useMemo(
    () => getCloudCvs([...savedCvs, ...candidateCvs]),
    [savedCvs, candidateCvs],
  );

  const cvSelectionValid = selectedCvUuids.every(uuid =>
    cloudCvs.some(cv => cv.uuid === uuid),
  );
  const cvBlocked = hasPendingCv || !cvSelectionValid;

  const downloadCv = async (uuid: string, name: string) => {
    if (downloadInFlight.current) return;
    downloadInFlight.current = true;
    setDownloadingCv(uuid);
    try {
      const { data } = await attachmentService.getAttachmentDownload({
        id: uuid,
      });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      errorToast(t('calendarEvent.cvDownloadError'));
    } finally {
      downloadInFlight.current = false;
      setDownloadingCv(null);
    }
  };

  useEffect(() => {
    if (!isEdit || !googleEventId) {
      setIsEventReady(true);
      setEventLoadError(false);
      return;
    }

    let cancelled = false;
    setIsEventReady(false);
    setEventLoadError(false);
    setIsSubmitting(true);

    dispatch(getCalendarEventThunk(googleEventId))
      .then((result: unknown) => {
        if (cancelled) return;
        if (!result || typeof result !== 'object') {
          setEventLoadError(true);
          errorToastRef.current(tRef.current('calendarEvent.loadErrorMessage'));
          return;
        }
        const event = result as CalendarEventFormData;
        const nextCandidateEmail = candidateEmail || '';
        const currentRecord = recordRef.current;

        const eventParticipants: CalendarEventParticipant[] = (
          event.participants || []
        ).map(participant => ({
          email: participant.email,
          fullName: participant.email,
          role: resolveParticipantRole(
            participant.email,
            nextCandidateEmail,
            currentRecord,
          ),
        }));

        if (
          nextCandidateEmail &&
          !eventParticipants.some(p => p.role === ParticipantRole.CANDIDATE)
        ) {
          eventParticipants.unshift({
            email: nextCandidateEmail,
            fullName: candidateName || nextCandidateEmail,
            role: ParticipantRole.CANDIDATE,
          });
        }

        const hasNonCandidateWithEmail = eventParticipants.some(
          p =>
            p.role !== ParticipantRole.CANDIDATE &&
            !!p.email.trim() &&
            new RegExp(REGEXP.EMAIL.regExp).test(p.email.trim()),
        );

        if (!hasNonCandidateWithEmail) {
          const appendEmployee = (
            employee: Employee | undefined,
            role: ParticipantRole,
          ) => {
            if (!employee?.email) return;
            const email = employee.email;
            if (
              eventParticipants.some(
                p => normalizeEmail(p.email) === normalizeEmail(email),
              )
            ) {
              return;
            }
            eventParticipants.push({
              email,
              fullName: employee.fullName,
              role,
            });
          };

          appendEmployee(currentRecord.hr, ParticipantRole.HR);
          currentRecord.techSpecialists?.forEach(ts =>
            appendEmployee(ts, ParticipantRole.TECH_SPECIALIST),
          );
        }

        setSummary(event.title || '');
        setDescription(event.description || '');
        if (event.startTime) setStartAt(dayjs(event.startTime).valueOf());
        if (event.endTime) setEndAt(dayjs(event.endTime).valueOf());
        if (event.timeZone) setSelectedTimeZone(event.timeZone);
        setParticipants(eventParticipants);
        setIsEventReady(true);
      })
      .finally(() => {
        if (!cancelled) setIsSubmitting(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isEdit,
    googleEventId,
    candidateEmail,
    candidateName,
    recordParticipantsKey,
    dispatch,
  ]);

  const timeZoneOptions = useMemo(
    () => getTimeZoneOptions(defaultTimeZone),
    [defaultTimeZone],
  );

  const selectedTimeZoneOption = useMemo(
    () =>
      timeZoneOptions
        .map(tz => ({ value: tz, label: tz }))
        .find(opt => opt.value === selectedTimeZone) || {
        value: selectedTimeZone,
        label: selectedTimeZone,
      },
    [selectedTimeZone, timeZoneOptions],
  );

  const additionalParticipantsCount = useMemo(
    () =>
      participants.filter(p => p.role === ParticipantRole.PARTICIPANT).length,
    [participants],
  );

  const isValidEmail = useCallback((email: string) => {
    return new RegExp(REGEXP.EMAIL.regExp).test(email.trim());
  }, []);

  const canAddExtraParticipant = useMemo(() => {
    const email = extraParticipantEmail.trim();
    return (
      additionalParticipantsCount < MAX_EXTRA_PARTICIPANTS &&
      !!email &&
      !extraParticipantEmailValidationMessage
    );
  }, [
    additionalParticipantsCount,
    extraParticipantEmail,
    extraParticipantEmailValidationMessage,
  ]);

  const requiredRoles = useMemo(() => {
    if (isEdit) return [ParticipantRole.CANDIDATE];

    if (eventStatusCode === EventStatusCode.INTERVIEW_PLANNED) {
      return [
        ParticipantRole.CANDIDATE,
        ParticipantRole.HR,
        ParticipantRole.TECH_SPECIALIST,
      ];
    }

    return [ParticipantRole.CANDIDATE, ParticipantRole.HR];
  }, [eventStatusCode, isEdit]);

  const candidateEmailToSend = useMemo(
    () =>
      participants.find(p => p.role === ParticipantRole.CANDIDATE)?.email ||
      candidateEmail ||
      '',
    [participants, candidateEmail],
  );

  const isCandidateEmailValid = useMemo(
    () => isValidEmail(candidateEmailToSend.trim()),
    [candidateEmailToSend, isValidEmail],
  );

  const summaryError = summary.trim()
    ? ''
    : t('calendarEvent.errors.summaryRequired');

  const startAtError =
    startAt < Date.now() ? t('calendarEvent.errors.startInPast') : '';

  const endAtError =
    endAt <= startAt ? t('calendarEvent.errors.endBeforeStart') : '';

  const getParticipantError = useCallback(
    (role: ParticipantRole) => {
      const roleParticipants = participants.filter(p => p.role === role);

      if (!roleParticipants.length) {
        if (role === ParticipantRole.HR)
          return t('calendarEvent.errors.hrMissing');
        if (role === ParticipantRole.TECH_SPECIALIST)
          return t('calendarEvent.errors.techMissing');
        if (role === ParticipantRole.CANDIDATE)
          return t('calendarEvent.errors.candidateEmailMissing');
        return '';
      }

      const hasValidParticipant = roleParticipants.some(participant => {
        const email = participant.email.trim();
        return !!email && isValidEmail(email);
      });

      if (hasValidParticipant) return '';

      if (role === ParticipantRole.CANDIDATE) {
        return t('calendarEvent.errors.candidateEmailMissing');
      }
      return t('calendarEvent.errors.emailMissing');
    },
    [participants, isValidEmail, t],
  );

  const nonCandidateParticipantsForApi = useMemo(
    () =>
      participants
        .filter(p => p.role !== ParticipantRole.CANDIDATE)
        .map(p => ({ email: p.email.trim() }))
        .filter(p => isValidEmail(p.email)),
    [participants, isValidEmail],
  );

  const participantsRequiredError = useMemo(
    () =>
      nonCandidateParticipantsForApi.length > 0
        ? ''
        : t('calendarEvent.errors.participantsRequired'),
    [nonCandidateParticipantsForApi, t],
  );

  const participantFormErrors = useMemo(() => {
    const messages = requiredRoles
      .map(role => getParticipantError(role))
      .filter(Boolean);

    if (
      !isCandidateEmailValid &&
      !messages.includes(t('calendarEvent.errors.candidateEmailMissing'))
    ) {
      messages.push(t('calendarEvent.errors.candidateEmailInvalid'));
    }

    const hasRoleParticipantError = messages.some(
      message =>
        message === t('calendarEvent.errors.hrMissing') ||
        message === t('calendarEvent.errors.techMissing') ||
        message === t('calendarEvent.errors.emailMissing'),
    );

    if (participantsRequiredError && !hasRoleParticipantError) {
      messages.push(participantsRequiredError);
    }

    return Array.from(new Set(messages));
  }, [
    requiredRoles,
    getParticipantError,
    isCandidateEmailValid,
    participantsRequiredError,
    t,
  ]);

  const formErrors = useMemo(() => {
    const messages: string[] = [];
    if (eventLoadError) messages.push(t('calendarEvent.loadErrorMessage'));
    if (summaryError) messages.push(summaryError);
    if (startAtError) messages.push(startAtError);
    if (endAtError) messages.push(endAtError);
    messages.push(...participantFormErrors);
    return messages;
  }, [
    eventLoadError,
    summaryError,
    startAtError,
    endAtError,
    participantFormErrors,
    t,
  ]);

  const isFormValid =
    formErrors.length === 0 && (!isEdit || (isEventReady && !eventLoadError));

  const missingRoles = requiredRoles.filter(
    role => !participants.some(p => p.role === role),
  );

  const getRoleLabel = (role: CalendarEventParticipant['role']): string => {
    const roleLabels: Record<CalendarEventParticipant['role'], string> = {
      candidate: t('Candidate'),
      hr: t('Hr'),
      techSpecialist: t('Tech specialist'),
      participant: t('Participant'),
    };
    return roleLabels[role];
  };

  const startEditingParticipant = (index: number) => {
    setEditingParticipantIndex(index);
    setEditingEmailValue(participants[index].email);
    setEditingEmailError('');
  };

  const cancelEditingParticipant = () => {
    setEditingParticipantIndex(null);
    setEditingEmailValue('');
    setEditingEmailError('');
  };

  const confirmEditingParticipant = () => {
    if (editingParticipantIndex === null) return;
    const email = editingEmailValue.trim();
    if (!isValidEmail(email)) {
      setEditingEmailError(t(REGEXP.EMAIL.message));
      return;
    }
    const editedIndex = editingParticipantIndex;
    setParticipants(prev =>
      prev.map((participant, i) =>
        i === editedIndex
          ? { ...participant, email, emailConfirmed: true }
          : participant,
      ),
    );
    cancelEditingParticipant();
  };

  const addExtraParticipant = (email: string) => {
    if (additionalParticipantsCount >= MAX_EXTRA_PARTICIPANTS) return;
    if (!isValidEmail(email)) {
      setExtraParticipantEmailValidationMessage(t(REGEXP.EMAIL.message));
      return;
    }
    setParticipants(prev => [
      ...prev,
      { email, fullName: email, role: ParticipantRole.PARTICIPANT, emailConfirmed: true },
    ]);
    setExtraParticipantEmail('');
    setExtraParticipantEmailValidationMessage('');
  };

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || isUploadingCv || cvBlocked || !isFormValid) return;
    if (isEdit && (!isEventReady || eventLoadError)) return;
    setIsSubmitting(true);

    const otherParticipants = nonCandidateParticipantsForApi;
    const shouldSendAttachments = selectedCvUuids.length > 0;

    const payload = {
      title: summary.trim(),
      description: description || undefined,
      candidateEmail: candidateEmailToSend,
      startTime: formatDateTimeForApi(startAt),
      endTime: formatDateTimeForApi(endAt),
      timeZone: selectedTimeZone,
      participants: otherParticipants,
      ...(shouldSendAttachments ? { attachmentUuid: selectedCvUuids } : {}),
    };

    const request =
      isEdit && googleEventId
        ? updateCalendarEventThunk(googleEventId, payload)
        : createCalendarEventThunk({
            personId: record.personId,
            personRecordId: record.id,
            ...payload,
          });

    const result = await ApiWrapper(request);
    setIsSubmitting(false);

    if (result === false) {
      errorToast(
        t(
          isEdit
            ? 'calendarEvent.updateErrorMessage'
            : 'calendarEvent.errorMessage',
        ),
      );
      return;
    }

    successToast(
      t(
        isEdit
          ? 'calendarEvent.updateSuccessMessage'
          : 'calendarEvent.successMessage',
      ),
    );

    await dispatch(refreshPersonInterviewsThunk(record.personId));

    onSaved?.();

    const created = (result as { data?: CalendarInterview })?.data ?? result;
    const createdInterview = created as Partial<CalendarInterview>;

    dispatch(
      upsertInterviewLocalAction({
        personId: record.personId,
        interview: {
          ...(isEdit
            ? { id: interviewId, googleEventId }
            : {
                id: createdInterview.id,
                googleEventId: createdInterview.googleEventId,
                personRecordId: createdInterview.personRecordId || record.id,
                meetLink: createdInterview.meetLink,
              }),
          title: payload.title,
          description: payload.description,
          startTime: dayjs(startAt).toISOString(),
          endTime: dayjs(endAt).toISOString(),
        },
      }),
    );

    closeModal();
  }, [
    isSubmitting,
    isFormValid,
    isEventReady,
    eventLoadError,
    nonCandidateParticipantsForApi,
    summary,
    description,
    candidateEmailToSend,
    startAt,
    endAt,
    selectedTimeZone,
    selectedCvUuids,
    cvBlocked,
    isUploadingCv,
    isEdit,
    googleEventId,
    interviewId,
    record.personId,
    record.id,
    onSaved,
    dispatch,
    closeModal,
    errorToast,
    successToast,
    t,
  ]);

  const renderFooter = useMemo(
    () => (
      <StyledFooter>
        {formErrors.length > 0 && (
          <StyledFormErrors>
            {formErrors.map(message => (
              <li key={message}>{message}</li>
            ))}
          </StyledFormErrors>
        )}
        <StyledFooterActions>
          <ButtonComponent
            appearance="primary"
            disabled={
              isSubmitting ||
              isUploadingCv ||
              cvBlocked ||
              !isFormValid ||
              eventLoadError ||
              !isEventReady ||
              editingParticipantIndex !== null
            }
            handleClick={handleSubmit}
          >
            {t(isEdit ? 'calendarEvent.save' : 'calendarEvent.create')}
          </ButtonComponent>
          <ButtonComponent handleClick={closeModal}>
            {t('calendarEvent.decline')}
          </ButtonComponent>
        </StyledFooterActions>
      </StyledFooter>
    ),
    [
      formErrors,
      isSubmitting,
      isUploadingCv,
      cvBlocked,
      isFormValid,
      eventLoadError,
      isEventReady,
      editingParticipantIndex,
      handleSubmit,
      isEdit,
      closeModal,
      t,
    ],
  );

  return (
    <StyledGoogleCalendarEventModal>
      <BaseModalComponent
        header={t(isEdit ? 'calendarEvent.editTitle' : 'calendarEvent.title')}
        footer={renderFooter}
      >
        <FormLayoutComponent>
          <InputComponent
            label={t('calendarEvent.summary')}
            value={summary}
            onChange={(value: string) => setSummary(value)}
            maxLength={200}
            validationFailureMessage={summaryError}
          />
          <InputComponent
            label={t('calendarEvent.startAt')}
            value={dayjs(startAt).format(DATE_TIME_FORMAT)}
            validationFailureMessage={startAtError}
            onClick={() =>
              openModal(DatepickerModalComponent, {
                defaultDate: startAt,
                handleChange: (date: number) => {
                  setStartAt(date);
                  if (date >= endAt) {
                    setEndAt(
                      date + DEFAULT_INTERVIEW_DURATION_HOURS * 60 * 60 * 1000,
                    );
                  }
                },
              })
            }
          />
          <InputComponent
            label={t('calendarEvent.endAt')}
            value={dayjs(endAt).format(DATE_TIME_FORMAT)}
            validationFailureMessage={endAtError}
            onClick={() =>
              openModal(DatepickerModalComponent, {
                defaultDate: endAt,
                handleChange: (date: number) => setEndAt(date),
              })
            }
          />
          <SelectComponent
            label={t('calendarEvent.timeZone')}
            value={selectedTimeZoneOption}
            placeholder={t('calendarEvent.timeZone')}
            options={[]}
            defaultOptions={timeZoneOptions.map(tz => ({
              value: tz,
              label: tz,
            }))}
            loadOptions={async (search: string) => {
              const normalized = (search || '').trim().toLowerCase();
              const filtered = normalized
                ? timeZoneOptions.filter(tz =>
                    tz.toLowerCase().includes(normalized),
                  )
                : timeZoneOptions;
              return {
                options: filtered.map(tz => ({ value: tz, label: tz })),
                hasMore: false,
              };
            }}
            isSearchable
            isClearable={false}
            onChangeOption={(option: SelectOptionProps | string) => {
              const nextTz =
                typeof option === 'string' ? option : option?.value;
              if (typeof nextTz === 'string') setSelectedTimeZone(nextTz);
            }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <StyledLabel>{t('calendarEvent.participants')}</StyledLabel>
            <StyledParticipantsList>
              {participants.map((participant, index) => {
                const isEditing = editingParticipantIndex === index;
                const isEditableRole =
                  participant.role !== ParticipantRole.PARTICIPANT;

                return (
                  <StyledParticipantItem key={`${participant.role}-${index}`}>
                    <StyledParticipantInfo>
                      <StyledParticipantName>
                        {participant.fullName}
                      </StyledParticipantName>
                      {isEditing ? (
                        <StyledParticipantEmailEdit>
                          <InputComponent
                            value={editingEmailValue}
                            autoFocus
                            validationFailureMessage={editingEmailError}
                            onChange={(value: string) => {
                              setEditingEmailValue(value);
                              setEditingEmailError('');
                            }}
                            onEnter={confirmEditingParticipant}
                            placeholder="email@example.com"
                          />
                        </StyledParticipantEmailEdit>
                      ) : (
                        <>
                          <StyledParticipantEmail>
                            {participant.email}
                          </StyledParticipantEmail>
                          {getParticipantError(participant.role) && (
                            <div style={{ color: 'red', fontSize: 11 }}>
                              {getParticipantError(participant.role)}
                            </div>
                          )}
                        </>
                      )}
                    </StyledParticipantInfo>
                    {!isEditing && (
                      <StyledParticipantRole>
                        {getRoleLabel(participant.role)}
                      </StyledParticipantRole>
                    )}
                    <StyledParticipantActions>
                      {isEditing ? (
                        <>
                          <StyledRemoveButton
                            onClick={confirmEditingParticipant}
                          >
                            <CheckIcon />
                          </StyledRemoveButton>
                          <StyledRemoveButton
                            onClick={cancelEditingParticipant}
                          >
                            <CrossIcon />
                          </StyledRemoveButton>
                        </>
                      ) : (
                        <>
                          {isEditableRole && (
                            <StyledRemoveButton
                              onClick={() => startEditingParticipant(index)}
                            >
                              <PenIcon />
                            </StyledRemoveButton>
                          )}
                          {participant.role === ParticipantRole.PARTICIPANT && (
                            <StyledRemoveButton
                              onClick={() =>
                                setParticipants(prev =>
                                  prev.filter((_, i) => i !== index),
                                )
                              }
                            >
                              <CrossIcon />
                            </StyledRemoveButton>
                          )}
                        </>
                      )}
                    </StyledParticipantActions>
                  </StyledParticipantItem>
                );
              })}
              {participants.length === 0 && (
                <div>{t('calendarEvent.noParticipants')}</div>
              )}
              {missingRoles.map(role => (
                <StyledParticipantItem key={`missing-${role}`}>
                  <StyledParticipantInfo>
                    <StyledParticipantName>
                      {getRoleLabel(role)}
                    </StyledParticipantName>
                    <StyledParticipantEmail style={{ color: 'red' }}>
                      {getParticipantError(role)}
                    </StyledParticipantEmail>
                  </StyledParticipantInfo>
                  <StyledParticipantRole>
                    {getRoleLabel(role)}
                  </StyledParticipantRole>
                </StyledParticipantItem>
              ))}
            </StyledParticipantsList>
          </div>

          <StyledInputParticipantContainer>
            <InputComponent
              label={t('calendarEvent.addParticipantByEmail')}
              value={extraParticipantEmail}
              disabled={additionalParticipantsCount >= MAX_EXTRA_PARTICIPANTS}
              validationFailureMessage={extraParticipantEmailValidationMessage}
              onChange={(value: string) => {
                setExtraParticipantEmail(value);
                const email = value.trim();
                if (!email) {
                  setExtraParticipantEmailValidationMessage('');
                  return;
                }
                setExtraParticipantEmailValidationMessage(
                  isValidEmail(email) ? '' : t(REGEXP.EMAIL.message),
                );
              }}
              onEnter={(value: string) => {
                const email = value.trim();
                if (email) addExtraParticipant(email);
              }}
              placeholder="email@example.com"
            />
            <ButtonComponent
              handleClick={() => {
                const email = extraParticipantEmail.trim();
                if (email) addExtraParticipant(email);
              }}
              disabled={!canAddExtraParticipant}
            >
              {t('calendarEvent.addParticipantButton')}
            </ButtonComponent>
          </StyledInputParticipantContainer>

          <StyledCvSection>
            <StyledLabel>{t('calendarEvent.attachCv')}</StyledLabel>
            {isEdit && (
              <StyledEmptyHint>
                {t('calendarEvent.existingCvHint')}
              </StyledEmptyHint>
            )}
            {isCandidateCvsLoading && cloudCvs.length === 0 ? (
              <StyledEmptyHint>{t('calendarEvent.loadingCvs')}</StyledEmptyHint>
            ) : cloudCvs.length > 0 ? (
              <StyledCvList>
                {cloudCvs.map(cv => (
                  <StyledCvItem key={cv.uuid}>
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedCvUuids.includes(cv.uuid)}
                        disabled={isUploadingCv || isSubmitting}
                        onChange={() => toggleCv(cv.uuid)}
                      />
                      {cvNames[cv.uuid] ||
                        getAttachmentFileName(cv.url) ||
                        t('Resume')}
                      {cv.comment && (
                        <StyledEmptyHint>{cv.comment}</StyledEmptyHint>
                      )}
                    </label>
                    <ButtonComponent
                      disabled={downloadingCv !== null}
                      handleClick={() =>
                        downloadCv(
                          cv.uuid,
                          cvNames[cv.uuid] ||
                            getAttachmentFileName(cv.url) ||
                            'CV',
                        )
                      }
                    >
                      {t(
                        downloadingCv === cv.uuid
                          ? 'calendarEvent.cvDownloading'
                          : 'calendarEvent.downloadCv',
                      )}
                    </ButtonComponent>
                  </StyledCvItem>
                ))}
              </StyledCvList>
            ) : (
              <StyledEmptyHint>{t('calendarEvent.noCloudCv')}</StyledEmptyHint>
            )}

            {!cvSelectionValid && (
              <StyledCvUploadError role="alert">
                {t('calendarEvent.cvSelectionChanged')}
                <ButtonComponent
                  disabled={isSubmitting || isUploadingCv}
                  handleClick={() => setSelectedCvUuids([])}
                >
                  {t('calendarEvent.resetCvSelection')}
                </ButtonComponent>
              </StyledCvUploadError>
            )}
            <UploadCvInlineModal
              disabled={isUploadingCv || isSubmitting}
              isUploading={isUploadingCv}
              onUpload={handleUploadCv}
              onPendingChange={setHasPendingCv}
            />

            {cvUploadError && (
              <StyledCvUploadError>{cvUploadError}</StyledCvUploadError>
            )}
            <StyledEmptyHint>
              {t('calendarEvent.cvAudienceHint')}
            </StyledEmptyHint>
            {hasPendingCv && (
              <StyledEmptyHint>
                {t('calendarEvent.cvPendingHint')}
              </StyledEmptyHint>
            )}
          </StyledCvSection>

          <TextareaComponent
            label={t('calendarEvent.description')}
            value={description}
            onChange={(value: string) => setDescription(value)}
            maxLength={textAriasMaxLength.EXTENDED}
          />
        </FormLayoutComponent>
      </BaseModalComponent>
    </StyledGoogleCalendarEventModal>
  );
};

export default GoogleCalendarEventModalComponent;
